import {
  useRef,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { AnimatePresence } from "framer-motion";

import styles from "./Match.module.scss";
import { Column } from "../Column";
import { DrawingArea } from "../DrawingArea";
import {
  RELACIONAR_COLUMNAS_SOUNDS,
  TYPE_OF_COLUMN,
} from "../../utils/constants";
import { useMatch } from "../../contexts/MatchContext";

import { Fill, useTranslate, useGameSounds, shuffle } from "@educaplay/core";
import {
  finishGame,
  goToQuestion,
  setAnimating,
} from "@educaplay/store/slices/gameSlice";

export const Match = forwardRef(function Match(props, ref) {
  const { onAnswer, currentQuestion } = props;
  const currentScreen = currentQuestion.screenId + 1;
  const dispatch = useDispatch();

  const {
    selectedPair,
    wrongPair,
    selectLeft,
    selectRight,
    clearSelection,
    addCompletedPair,
    isCompleted,
    showWrongPair,
    addArrow,
    resetMatch,
    updateAllCirclePositions,
    completedPairs,
  } = useMatch();

  const t = useTranslate();

  const canvasRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const columnLeftRef = useRef(null);
  const columnRightRef = useRef(null);

  const totalScreens = useSelector(
    (state) =>
      state.game.questions[state.game.questions.length - 1].screenId + 1
  );
  const isAnimating = useSelector((state) => state.game.isAnimating);
  const limitedLives = useSelector((state) => state.game.liveActived);
  const currentScreenQuestions = useSelector((state) =>
    state.game.questions.filter(
      (pair) => pair.screenId === currentQuestion.screenId
    )
  );

  const pairCountText = useMemo(() => {
    return `${t("specific.pairs")}: ${completedPairs.size} / ${currentScreenQuestions.length
      } `;
  }, [t, completedPairs, currentScreenQuestions]);


  const screenCountText = useMemo(() => {
    return `${t("specific.screen")}: ${currentScreen}/${totalScreens}`;
  }, [t, currentScreen, totalScreens]);

  const gameSounds = useGameSounds();

  useEffect(() => {
    resetMatch();
  }, [currentScreen, resetMatch]);

  const handleLeftSizeChanged = useCallback(
    (positions) => {
      updateAllCirclePositions("left", positions);
    },
    [updateAllCirclePositions]
  );

  const handleRightSizeChanged = useCallback(
    (positions) => {
      updateAllCirclePositions("right", positions);
    },
    [updateAllCirclePositions]
  );

  useImperativeHandle(ref, () => ({
    animateEntry: async () => {
      await Promise.all([
        columnLeftRef.current?.animateEntry(),
        columnRightRef.current?.animateEntry(),
      ]);
    },
    animateExit: async () => {
      await exitAnimation();
    },
  }));

  const exitAnimation = async () => {
    await Promise.all([
      columnLeftRef.current?.animateExit(),
      columnRightRef.current?.animateExit(),
    ]);
  };

  const leftElements = useMemo(
    () =>
      shuffle(
        currentScreenQuestions.map((current) => {
          return { ...current.leftElement, id: current.id };
        })
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentScreen]
  );

  const rightElements = useMemo(
    () =>
      shuffle(
        currentScreenQuestions.map((current) => {
          return { ...current.rightElement, id: current.id };
        })
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentScreen]
  );

  function handleSelectLeft(id, checked) {
    if (isAnimating) return;
    const newValue = checked ? id : null;
    selectLeft(newValue);
    if (checked && selectedPair.right !== null) {
      checkCorrect(newValue, selectedPair.right);
    }
  }

  function handleSelectRight(id, checked) {
    if (isAnimating) return;
    const newValue = checked ? id : null;
    selectRight(newValue);
    if (checked && selectedPair.left !== null) {
      checkCorrect(selectedPair.left, newValue);
    }
  }

  const checkCorrect = async (left, right) => {
    if (isAnimating) return;
    dispatch(setAnimating(true));

    if (left === null || right === null || isNaN(left) || isNaN(right)) {
      gameSounds.play(RELACIONAR_COLUMNAS_SOUNDS.ACTIVO);
      dispatch(setAnimating(false));
      return;
    }

    const isLastPairOnScreen =
      completedPairs.size + 1 === currentScreenQuestions.length;
    const isFinalScreen = currentScreen === totalScreens;

    if (left === right) {
      addCompletedPair(left);
      gameSounds.play(RELACIONAR_COLUMNAS_SOUNDS.CORRECTO);

      if (!isLastPairOnScreen) {
        addArrow(left);
      } else {
        addArrow(left);

        if (!isFinalScreen) {
          await exitAnimation();
        }
      }
    } else {
      gameSounds.play(RELACIONAR_COLUMNAS_SOUNDS.MOVIENDO);
      showWrongPair(left, right);
    }

    const next = isLastPairOnScreen
      ? currentScreenQuestions.at(-1).id + 1
      : left;
    const screenControl = next !== left && isFinalScreen;

    dispatch(goToQuestion(left));

    const tries = currentScreenQuestions.reduce(
      (acc, question) => {
        const triesLeft = question.tries.filter(
          (intento) => intento.left === left
        ).length;
        const triesRight = question.tries.filter(
          (intento) => intento.right === right
        ).length;
        return {
          ...acc,
          left: acc.left + triesLeft,
          right: acc.right + triesRight,
        };
      },
      { left: 0, right: 0 }
    );
    await onAnswer(
      { left, right, tries },
      {
        nextQuestionIndex: screenControl ? left : next,
        playErrorSound: limitedLives,
      }
    );

    if (screenControl) {
      dispatch(finishGame());
      dispatch(setAnimating(false));
      return;
    }

    clearSelection();
    dispatch(setAnimating(false));
  };

  function handleDragMatch(startCardId, startSide, endCardId, endSide) {
    if (isAnimating) return;
    if (startSide === endSide) {
      gameSounds.play(RELACIONAR_COLUMNAS_SOUNDS.MOVIENDO);
      clearSelection();
      return;
    }

    const leftId = startSide === TYPE_OF_COLUMN.LEFT ? startCardId : endCardId;
    const rightId = startSide === TYPE_OF_COLUMN.LEFT ? endCardId : startCardId;

    checkCorrect(leftId, rightId);
    clearSelection();
  }

  return (
    <>
      <Fill name={"core-bank-right"}>{pairCountText}</Fill>
      <Fill name={"core-bank-left"}>{screenCountText}</Fill>
      <DrawingArea
        key={currentScreen}
        canvasRef={canvasRef}
        scrollContainerRef={scrollContainerRef}
        onDragMatch={handleDragMatch}
      >
        <AnimatePresence>
          <Column
            key="left"
            ref={columnLeftRef}
            elements={leftElements}
            onSelect={handleSelectLeft}
            selectedElement={selectedPair.left}
            disabled={isCompleted}
            className={styles.card__left}
            position={TYPE_OF_COLUMN.LEFT}
            onSizeChanged={handleLeftSizeChanged}
            wrong={wrongPair?.left}
            limLives={limitedLives}
            scrollContainerRef={scrollContainerRef}
          />
        </AnimatePresence>

        <AnimatePresence>
          <Column
            key="right"
            ref={columnRightRef}
            elements={rightElements}
            onSelect={handleSelectRight}
            selectedElement={selectedPair.right}
            disabled={isCompleted}
            position={TYPE_OF_COLUMN.RIGHT}
            onSizeChanged={handleRightSizeChanged}
            wrong={wrongPair?.right}
            limLives={limitedLives}
            scrollContainerRef={scrollContainerRef}
          />
        </AnimatePresence>
      </DrawingArea>
    </>
  );
});
